import { inject } from '@angular/core';
import { HttpRequest, HttpEvent, HttpErrorResponse, HttpInterceptorFn, HttpHandlerFn } from '@angular/common/http';
import { catchError, EMPTY, filter, Observable, switchMap, take, throwError } from 'rxjs';
import { ToastItem } from 'projects/shared/lib/components/shared/toast/item/toast-item.model';
import { EToastItemType } from '../enum/toast/toast-entry-type.enum';
import { IAuthService, IAuthServiceToken } from '../services/infrastructure/authentication/auth-service.interface';
import { IUserService, IUserServiceToken } from '../services/data/user/user-service.interface';
import { ErrorCodeService } from '../services/infrastructure/error-code.service';
import { EError } from '../services/INTEGRA_Shop_Client';
import { IIdpApiService, IIdpApiServiceToken } from '../services/api/idp/idp-api-service.interface';
import { ENVIRONMENT } from '../environment/environment';
import { IConfigurationService, IConfigurationServiceToken } from '../services/infrastructure/configuration/configuration-service.interface';
import { IToastService, IToastServiceToken } from 'shared/services/ui/toast/toast-service.interface';

// eslint-disable-next-line @typescript-eslint/naming-convention
const AuthenticationUrlListDefaults: string[] = [
  'UserLoginByRefreshToken',
  'UserLogout',
  'UserLogin'
];

// eslint-disable-next-line @typescript-eslint/naming-convention
const WithCredentialsAuthUrlListDefaults: string[] = [
  "/api/User/UserLogin",
  "/api/User/UserLoginByRefreshToken",
  "/api/User/UserPasswordSet",
  "/api/User/UserLogout"
];

// eslint-disable-next-line @typescript-eslint/naming-convention
export const httpAuthInterceptor: HttpInterceptorFn = (req, next) => {
  let authService = inject(IAuthServiceToken);
  let userService = inject(IUserServiceToken);
  let toastService = inject(IToastServiceToken);
  let idpApiService = inject(IIdpApiServiceToken, { optional: true });
  let configService = inject(IConfigurationServiceToken);

  req = addWithCredentialsHeader(req);

  let isAuthanticationUrl = checkIsAuthenticationAuthUrl(req.url);
  if (isAuthanticationUrl == true) {
    return next(req).pipe(
      catchError(
        (error) => handleException(authService, userService, idpApiService, toastService, configService, req, next, error)
      )
    );
  }

  if (authService.isAuthenticated() == true) {
    if (authService.accessJWTValidateFromLocalStorage() == false) {
      return refreshTokenAndRetry(authService, userService, idpApiService, toastService, configService, req, next);
    }
    req = requestSetAccessJWT(authService, req);
  }

  return next(req).pipe(
    catchError(
      (error) => handleException(authService, userService, idpApiService, toastService, configService, req, next, error)
    )
  );
};

function logout(authService: IAuthService, userService: IUserService, idpApiService: IIdpApiService | null, toastService: IToastService, displayToast: boolean, configService: IConfigurationService) {
  configurationRefresh(configService);
  authService.accessJWTClear();

  idpApiService?.logout();
  userService.userLogout().subscribe();

  if (displayToast) {
    toastService.toastItemAdd(new ToastItem({
      type: EToastItemType.Warning,
      title: $localize`:@@AuthInterceptorLoginByRefreshFailedTitle:Login failed`,
      message: $localize`:@@AuthInterceptorLoginByRefreshFailedMessage:Please login again.`,
      code: ErrorCodeService.formatErrorCode(EError.F200110),
    }));
  }
}

function checkIsAuthenticationAuthUrl(url: string) {
  let authenticationUrlListConfig: string[] = [];
  if (ENVIRONMENT.auth.authenticationUrlList != undefined) {
    authenticationUrlListConfig = ENVIRONMENT.auth.authenticationUrlList;
  }

  let authenticationUrlList = [...new Set([...AuthenticationUrlListDefaults, ...authenticationUrlListConfig])];

  return authenticationUrlList.some(u => url.includes(u));
}

function addWithCredentialsHeader(req: HttpRequest<unknown>): HttpRequest<unknown> {
  let withCredentialsAuthUrlListConfig: string[] = [];
  if (ENVIRONMENT.auth.withCredentialsAuthUrlList != undefined) {
    withCredentialsAuthUrlListConfig = ENVIRONMENT.auth.withCredentialsAuthUrlList;
  }

  let withCredentialsAuthUrlList = [...new Set([...WithCredentialsAuthUrlListDefaults, ...withCredentialsAuthUrlListConfig])];

  let addWithCredentialsHeader = withCredentialsAuthUrlList.some(
    (url) => req.url.includes(url)
  );

  if (addWithCredentialsHeader) {
    return req.clone({
      withCredentials: true
    });
  } else {
    return req;
  }
}

function handleException(authService: IAuthService, userService: IUserService, idpApiService: IIdpApiService | null, toastService: IToastService, configService: IConfigurationService, req: HttpRequest<unknown>, next: HttpHandlerFn, error: any): Observable<HttpEvent<unknown>> {
  if (error instanceof HttpErrorResponse) {
    switch (error.status) {
      case 401: return handle401Exception(authService, userService, idpApiService, toastService, configService, req, next, error);
      default:
        break;
    }
  }
  return throwError(() => error); // error not relevant here -> pass to next interceptor/handler
}

function handle401Exception(authService: IAuthService, userService: IUserService, idpApiService: IIdpApiService | null, toastService: IToastService, configService: IConfigurationService, req: HttpRequest<unknown>, next: HttpHandlerFn, error: any): Observable<HttpEvent<unknown>> {
  if (req.url.includes('UserLogin')) {
    authService.accessJWTRefreshInProgress = false;
    return throwError(() => error);
  } else if (req.url.includes('UserLoginByRefreshToken')) {
    authService.accessJWTRefreshInProgress = false;
    logout(authService, userService, idpApiService, toastService, false, configService);
    return EMPTY;
  } else if (req.url.includes('UserLogout')) {
    authService.accessJWTRefreshInProgress = false;
    return throwError(() => error);
  }
  else {
    let isJWTValid = authService.accessJWTValidateFromLocalStorage();

    if (isJWTValid == false) {
      return refreshTokenAndRetry(authService, userService, idpApiService, toastService, configService, req, next);
    } else {
      return throwError(() => error);
    }
  }
}

function refreshTokenAndRetry(authService: IAuthService, userService: IUserService, idpApiService: IIdpApiService | null, toastService: IToastService, configService: IConfigurationService, req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  if (authService.accessJWTRefreshInProgress) {
    return authService.accessJWTRefreshSubject.pipe(
      filter(
        (accessJWTLocal) => {
          return authService.accessJWTValidate(accessJWTLocal);
        }),
      take(1),
      switchMap(
        () => {
          return next(requestSetAccessJWT(authService, req)).pipe(
            catchError(
              (error) => handleException(authService, userService, idpApiService, toastService, configService, req, next, error)
            )
          );
        }
      ),
      // timeout(requestWaitForAccessJWTTimeoutInMilli)
    );
  } else {
    authService.accessJWTRefreshInProgress = true;
    authService.accessJWTRefreshSubject.next(undefined);

    return accessJWTRefresh(userService).pipe(
      switchMap(
        (token) => {
          let isLoginSuccessfull = authService.isAccessJWTValid(token);
          if (isLoginSuccessfull != true) {
            throw new Error("Login failed. JWT validation failed.");
          }

          authService.accessJWTSet(token);
          authService.accessJWTRefreshSubject.next(token);
          authService.accessJWTRefreshInProgress = false;
          return next(requestSetAccessJWT(authService, req))
            .pipe(
              catchError(
                (error) => handleException(authService, userService, idpApiService, toastService, configService, req, next, error)
              )
            )
            ;
        }
      ),
      catchError((error) => {
        authService.accessJWTRefreshInProgress = false;

        logout(authService, userService, idpApiService, toastService, false, configService);
        return throwError(() => error); // error not relevant here -> pass to next interceptor/handler
      })
    );
  }
}

function requestSetAccessJWT(authService: IAuthService, req: HttpRequest<unknown>): HttpRequest<unknown> {
  let authHeader = 'Authorization';
  let accessJWT = authService.accessJWTSubject.value;

  if (!accessJWT) {
    return req;
  }
  return req.clone({
    headers: req.headers.set(authHeader, "Bearer " + accessJWT)
  });
}

function configurationRefresh(configService: IConfigurationService) {
  configService.dynamicConfigurationLoad();
}

function accessJWTRefresh(userService: IUserService): Observable<string> {
  return userService.userLoginByRefreshToken();
}
